/* ============================================================================
 *  API adapters — Mentoring (creator side)
 * ----------------------------------------------------------------------------
 *  Normalise the creator-facing mentoring payloads (core-platform
 *  `Modules/Mentoring`, branch feat/mentoring-program-gaps) into the shapes
 *  the views were built against: nested duration objects → flat value/unit
 *  fields, flat banner fields → banner.{url,type}, flat dates → a timestamps
 *  object.
 * ========================================================================== */

/** Program summary nested in creator resources → view shape. */
function adaptProgramSummary(program) {
  if (!program) return null;
  return {
    id: program.id,
    name: program.name,
    description: program.description ?? "",
    split_percentage: program.split_percentage ?? null,
    duration_value: program.duration?.value ?? null,
    duration_unit: program.duration?.unit ?? null,
    split_duration_value: program.split_duration?.value ?? null,
    split_duration_unit: program.split_duration?.unit ?? null,
    contract_url: program.contract_url ?? null,
    banner: {
      url: program.banner_url ?? null,
      type: program.banner_type ?? "image",
    },
  };
}

/** CreatorEnrollmentListResource item → §6.0 shortcut / §6.11 card shape. */
export function adaptMyEnrollmentListItem(raw) {
  return {
    id: raw.id,
    status: raw.status,
    program: raw.program
      ? {
          id: raw.program.id,
          name: raw.program.name,
          banner: { url: raw.program.banner_url ?? null, type: "image" },
        }
      : null,
    community: raw.community
      ? {
          id: raw.community.id,
          name: raw.community.name,
          banner_url: raw.community.banner ?? null,
        }
      : null,
    timestamps: {
      started_at: raw.started_at ?? null,
      ends_at: raw.ends_at ?? null,
      split_ends_at: raw.split_ends_at ?? null,
      split_ended_at: raw.split_ended_at ?? null,
      ended_at: raw.ended_at ?? null,
    },
  };
}

/** CreatorEnrollmentDetailResource → §6.11 single / §6.10 attach shape. */
export function adaptMyEnrollmentDetail(raw) {
  if (!raw) return null;

  return {
    id: raw.id,
    status: raw.status,
    source: raw.source,
    split_percentage: raw.split_percentage ?? null,
    effective_split_percentage: raw.effective_split_percentage ?? null,
    notes: raw.notes ?? null,
    // The backend sends { action, message, url }; the views render the message.
    next_steps: raw.next_steps?.message ?? null,
    program: adaptProgramSummary(raw.program),
    community: raw.community
      ? {
          id: raw.community.id,
          name: raw.community.name,
          description: raw.community.description ?? "",
          banner_url: raw.community.banner ?? null,
        }
      : null,
    timestamps: {
      started_at: raw.started_at ?? null,
      ends_at: raw.ends_at ?? null,
      ended_at: raw.ended_at ?? null,
      split_ends_at: raw.split_ends_at ?? null,
      split_ended_at: raw.split_ended_at ?? null,
    },
  };
}

/**
 * MentoringInvitationPublicResource (flat) → the { program, enrollment }
 * wrapper the §6.9 invite page renders.
 */
export function adaptPublicInvitation(raw) {
  if (!raw) return null;

  return {
    program: {
      id: raw.program_id ?? null,
      name: raw.program_name,
      description: raw.program_description ?? "",
      split_percentage: raw.split_percentage ?? null,
      duration_value: raw.duration?.value ?? null,
      duration_unit: raw.duration?.unit ?? null,
      split_duration_value: raw.split_duration?.value ?? null,
      split_duration_unit: raw.split_duration?.unit ?? null,
      contract_url: raw.contract_url ?? null,
      banner: { url: raw.banner_url ?? null, type: raw.banner_type ?? "image" },
    },
    enrollment: {
      id: raw.enrollment_id ?? null,
      status: raw.status,
      invited_email: raw.invited_email,
      timestamps: { invite_token_expires_at: raw.expires_at ?? null },
    },
  };
}
